import {useEffect, useState} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Skeleton,
    IconButton,
    Stack,
    FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {TaxRepService, type TaxRepBO} from '@/services/manageService/taxRepService';
import {InputField, TaxRepBrField, TaxRepTelephoneField, validateTaxRepBr} from '@/components/common';
import {defaultTaxRepPhone, isCompleteTaxRepPhone, normalizeTaxRepPhone} from '@/utils/taxRepPhone';

interface Props {
    open: boolean;
    onClose: (payload?: { refreshed?: boolean; message?: string; severity?: 'success' | 'error' }) => void;
    taxRepId?: number;
}

const EMPTY_FORM = {
    taxRepName: '',
    telephone: defaultTaxRepPhone(),
    herebyAuthorize: '',
    address: '',
    brNumber: '',
};

export default function TaxRepDetail({open, onClose, taxRepId}: Props) {
    const isEdit = !!taxRepId;
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [fieldErrors, setFieldErrors] = useState<{
        taxRepName?: string;
        telephone?: string;
        brNumber?: string;
        _global?: string;
    }>({});

    const loadDetail = async () => {
        if (!taxRepId) return;
        try {
            setLoading(true);
            const data = await TaxRepService.getById(taxRepId);
            setForm({
                taxRepName: data.taxRepName ?? '',
                telephone: normalizeTaxRepPhone(data.telephone ?? ''),
                herebyAuthorize: data.herebyAuthorize ?? '',
                address: data.address ?? '',
                brNumber: data.brNumber ?? '',
            });
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            if (isEdit) {
                loadDetail();
            } else {
                setForm({...EMPTY_FORM, telephone: defaultTaxRepPhone()});
            }
            setFieldErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, taxRepId]);

    const validate = (): boolean => {
        const errors: typeof fieldErrors = {};
        if (!form.taxRepName.trim()) errors.taxRepName = 'Tax Representative is required';
        if (!isCompleteTaxRepPhone(form.telephone)) {
            errors.telephone = 'Enter a valid telephone, e.g. (852)37569536';
        }
        if (form.brNumber && !validateTaxRepBr(form.brNumber)) {
            errors.brNumber = 'BR prefix (before "-") is required';
        }
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const buildBo = (): TaxRepBO => ({
        taxRepName: form.taxRepName.trim(),
        telephone: normalizeTaxRepPhone(form.telephone).trim(),
        herebyAuthorize: form.herebyAuthorize.trim(),
        address: form.address.trim(),
        brNumber: form.brNumber.trim(),
    });

    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const bo = buildBo();
            if (isEdit && taxRepId) {
                await TaxRepService.update(taxRepId, bo);
            } else {
                await TaxRepService.create(bo);
            }
            onClose({refreshed: true, message: 'Saved successfully', severity: 'success'});
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : 'Save failed';
            setFieldErrors({_global: message});
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="sm">
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {isEdit ? 'Edit Tax Representative' : 'Create Tax Representative'}
                    <IconButton size="small" onClick={() => onClose()}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Stack spacing={2} mt={1}>
                        {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="rounded" height={40}/>)}
                    </Stack>
                ) : (
                    <Stack spacing={2} mt={1}>
                        <InputField size="small" label="Tax Representative" required
                                    error={!!fieldErrors.taxRepName}
                                    helperText={fieldErrors.taxRepName}
                                    value={form.taxRepName}
                                    onChange={(e) => setForm({...form, taxRepName: e.target.value})}/>
                        <TaxRepTelephoneField
                            size="small"
                            label="Telephone Number"
                            value={form.telephone}
                            error={!!fieldErrors.telephone}
                            helperText={fieldErrors.telephone}
                            onChange={(next) => {
                                setForm({...form, telephone: next});
                                if (fieldErrors.telephone && isCompleteTaxRepPhone(next)) {
                                    setFieldErrors({...fieldErrors, telephone: undefined});
                                }
                            }}
                        />
                        <InputField size="small" label="Hereby authorize"
                                    value={form.herebyAuthorize}
                                    onChange={(e) => setForm({...form, herebyAuthorize: e.target.value})}/>
                        <InputField size="small" label="of (Address)"
                                    value={form.address}
                                    onChange={(e) => setForm({...form, address: e.target.value})}/>
                        <TaxRepBrField
                            size="small"
                            value={form.brNumber}
                            error={!!fieldErrors.brNumber || (Boolean(form.brNumber) && !validateTaxRepBr(form.brNumber))}
                            helperText={fieldErrors.brNumber || ((form.brNumber && !validateTaxRepBr(form.brNumber)) ? 'BR prefix is required' : '')}
                            onChange={(next) => {
                                setForm({...form, brNumber: next});
                                if (fieldErrors.brNumber && validateTaxRepBr(next)) {
                                    setFieldErrors({...fieldErrors, brNumber: undefined});
                                }
                            }}
                        />
                        {fieldErrors._global && (
                            <FormHelperText error>{fieldErrors._global}</FormHelperText>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button variant="contained" onClick={onSave} disabled={saving || loading}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
